import React, { useState } from 'react';
import type {
  FormBlurEventFn,
  FormChangeEventFn,
  HasValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import { TextButton } from '@noths/polaris-client-ribbons-design-system';

import { Dropdown } from 'src/components/atoms/Dropdown/Dropdown';
import { TextInput } from 'src/components/atoms/TextInput/TextInput';
import { AddressSearch } from 'src/components/molecules/AddressSearch/components/AddressSearch';
import { COUNTRY_CODES } from 'src/components/organisms/Signup/constants/aboutYouOptions';
import { ENTER_ADDRESS_MANUALLY_COPY } from 'src/components/organisms/Signup/constants/copy';
import {
  ADDRESS_COUNTRY_TESTID,
  ADDRESS_LINE_1_TESTID,
  ADDRESS_LINE_2_TESTID,
  ADDRESS_POSTCODE_TESTID,
  ADDRESS_TOWN_TESTID,
} from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import { useTracking } from 'src/hooks/useTracking/useTracking';
import * as styles from 'src/styles/styles';
import type { PostalAddress, RegistrationForm } from 'src/types/registration';

interface Props {
  clearError: (errorName: string) => void;
  errors: FormValidationErrors;
  formValues: RegistrationForm;
  hasValidation: HasValidationFn;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  setFormValue: SetFormValueFn<RegistrationForm>;
  validatableFields: readonly string[];
}

const ADDRESS_SEARCH_NAME = 'address-search';
const ADDRESS_SEARCH_DATA_SECTION = '1';
const ADDRESS_SEARCH_DATA_POSITION = '3';
const ENTER_ADDRESS_MANUALLY_NAME = 'enter-address-manually';

export const CompanyTradingAddress = ({
  clearError,
  errors,
  formValues,
  hasValidation,
  onBlur,
  onFormChange,
  setFormValue,
  validatableFields,
}: Props) => {
  const hasAddressAlready = Boolean(formValues.address.addressLine1);
  const [showFullAddress, setShowFullAddress] = useState(hasAddressAlready);
  const { trackAction, trackCompleted, trackEvent, trackFocusOnce } = useTracking();

  return (
    <>
      <p css={styles.label}>Your company&apos;s trading address</p>
      {!showFullAddress ? (
        <>
          <AddressSearch
            country="GB,IE"
            data-position={ADDRESS_SEARCH_DATA_POSITION}
            data-section={ADDRESS_SEARCH_DATA_SECTION}
            error={errors['address.addressLine1'] as string | undefined}
            name={ADDRESS_SEARCH_NAME}
            onAddressSelected={(selectedAddress) => {
              setShowFullAddress(true);

              const address = { ...formValues.address };
              address.addressLine1 = selectedAddress.address1;
              address.addressLine2 = selectedAddress.address2;
              address.addressTown = selectedAddress.town;
              address.postcode = selectedAddress.postcode;
              address.country = selectedAddress.country;

              setFormValue('address', address as PostalAddress);

              const validatableAddressFields = validatableFields.filter((field) =>
                field.startsWith('address.'),
              );

              validatableAddressFields.forEach((field) => {
                if (hasValidation(field)) {
                  clearError(field);
                }
              });

              trackEvent({
                action: 'Address Finder Selection',
                label: ADDRESS_SEARCH_NAME,
                fieldDetails: {
                  field_name: ADDRESS_SEARCH_NAME,
                  field_type: 'text',
                  field_position: ADDRESS_SEARCH_DATA_POSITION,
                  form_section: `Section ${ADDRESS_SEARCH_DATA_SECTION}`,
                  required_field: false,
                },
              });
            }}
            onBlur={(event) => trackCompleted(event)}
            onFocus={(event) => trackFocusOnce(event)}
            onQueryChanged={(event) => trackAction({ event, action: 'Find Address' })}
          />
          <p css={[styles.label, styles.normalFontWeight]}>Or</p>
          <TextButton
            css={styles.enterAddressManually}
            id="trading-address-manual-entry"
            name={ENTER_ADDRESS_MANUALLY_NAME}
            onClick={(e) => {
              e.preventDefault();
              setShowFullAddress(true);

              trackEvent({
                action: 'Enter Address Manually Click',
                label: ENTER_ADDRESS_MANUALLY_NAME,
                fieldDetails: {
                  field_name: ENTER_ADDRESS_MANUALLY_NAME,
                  field_type: 'button',
                  field_position: '3.1',
                  form_section: `Section ${ADDRESS_SEARCH_DATA_SECTION}`,
                  required_field: false,
                },
              });
            }}
          >
            {ENTER_ADDRESS_MANUALLY_COPY}
          </TextButton>
        </>
      ) : (
        <div css={[styles.growIn, styles.businessAddress]}>
          <TextInput
            css={styles.shortField}
            data-position="3.2"
            data-section="1"
            data-testid={ADDRESS_LINE_1_TESTID}
            error={errors['address.addressLine1'] as string | undefined}
            id="partner-company-tradingAddressLine1"
            label=""
            name="address.addressLine1"
            onBlur={onBlur}
            onChange={onFormChange}
            placeholder="Address line 1"
            required
            value={formValues.address.addressLine1}
          />
          <TextInput
            css={styles.shortField}
            data-position="3.3"
            data-section="1"
            data-testid={ADDRESS_LINE_2_TESTID}
            error={errors['address.addressLine2'] as string | undefined}
            id="partner-company-tradingAddressLine2"
            label=""
            name="address.addressLine2"
            onChange={onFormChange}
            placeholder="Address line 2"
            value={formValues.address.addressLine2}
          />
          <TextInput
            css={styles.shortField}
            data-position="3.4"
            data-section="1"
            data-testid={ADDRESS_TOWN_TESTID}
            error={errors['address.addressTown'] as string | undefined}
            id="partner-company-tradingAddressTown"
            label=""
            name="address.addressTown"
            onBlur={onBlur}
            onChange={onFormChange}
            placeholder="Town or city"
            required
            value={formValues.address.addressTown}
          />
          <TextInput
            css={styles.shortField}
            data-position="3.5"
            data-section="1"
            data-testid={ADDRESS_POSTCODE_TESTID}
            error={errors['address.postcode'] as string | undefined}
            id="trading-address-postcode"
            label=""
            name="address.postcode"
            onBlur={onBlur}
            onChange={onFormChange}
            placeholder="Post code"
            required
            value={formValues.address.postcode}
          />
          <Dropdown
            css={styles.shortField}
            data-position="3.6"
            data-section="1"
            data-testid={ADDRESS_COUNTRY_TESTID}
            error={errors['address.country'] as string | undefined}
            id="trading-address-country"
            label=""
            labelHidden
            name="address.country"
            onBlur={onBlur}
            onChange={onFormChange}
            options={COUNTRY_CODES}
            required
            value={formValues.address.country}
          />
        </div>
      )}
    </>
  );
};
